/**
 * The pure half of "which rows are still being scored" (PREC-4076).
 *
 * The API is asked about the rows the table is currently showing rather than about the whole list,
 * because a list being built can hold tens of thousands of leads and this question is polled.
 */

/** Matches the DTO's `ArrayMaxSize`; a page is far smaller, this is a guard, not a page size. */
export const PENDING_LEAD_SCORING_MAX_ROWS = 500;

/** How often to re-ask while something is still queued. Scoring a lead takes ~100 s. */
export const PENDING_LEAD_SCORING_POLL_MS = 15_000;

export interface PendingLeadRow {
	listPersonId: number;
	prospectId: number;
}

interface InboxRow {
	id?: number;
	prospect?: { id?: number } | null;
}

/**
 * The rows worth asking about. A lead with no prospect cannot have a scoring job — the job is
 * addressed by prospect — so it is dropped rather than sent as a hole the server has to reject.
 */
export const toPendingLeadRows = (inbox: InboxRow[] | undefined): PendingLeadRow[] => {
	if (!inbox?.length) {
		return [];
	}

	const seen = new Set<number>();
	const rows: PendingLeadRow[] = [];

	for (const row of inbox) {
		const listPersonId = row?.id;
		const prospectId = row?.prospect?.id;

		if (!listPersonId || !prospectId || seen.has(listPersonId)) {
			continue;
		}

		seen.add(listPersonId);
		rows.push({ listPersonId, prospectId });

		if (rows.length === PENDING_LEAD_SCORING_MAX_ROWS) {
			break;
		}
	}

	return rows;
};

/**
 * The leads that were queued a moment ago and are not any more — the ones whose score has just
 * changed on the server. Returning them is what tells the caller to pull the rows again; without
 * that the spinner would stop and uncover the *old* score.
 */
export const settledListPersonIds = (
	previous: number[] | undefined,
	current: number[] | undefined
): number[] => {
	if (!previous?.length) {
		return [];
	}

	const stillPending = new Set(current ?? []);

	return previous.filter((listPersonId) => !stillPending.has(listPersonId));
};
